import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft, Search, Sparkles, Mic, X, ShoppingBag, Plus, Minus, Check, ChevronLeft, ChevronRight, PackageOpen, ArrowRight,
} from "lucide-react";
import { AppDispatch } from "../../redux/store";
import { listenToPublishedProducts, Product } from "../../redux/productSlice";
import { addToCart, decrementCartItem } from "../../redux/cartSlice";
import { AllCategoriesFlat, CategoryItem } from "./Categories";
import BAi from "./BAi";

/* ── CART ADAPTER (same as Shop) ── */
const selectCartItems = (s: any): any[] => s.cart?.items ?? [];
const lineQty = (i: any): number => Number(i?.quantity ?? i?.qty ?? 1);
const lineId = (i: any): string => String(i?.productId ?? i?.listingId ?? "");

const MAKEUP_ID = "0102";

/* ═══════════════════════════ HELPERS ═══════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));
const childrenOf = (parentId: string) => AllCategoriesFlat.filter((c) => c.parentId === parentId);
const idOf = (v: any): string | null => (v === undefined || v === null || v === "" ? null : String(v));

const chainIdsOf = (id?: string | null): string[] => {
  const chain: string[] = [];
  const seen = new Set<string>();
  let cur = id ? CAT_MAP.get(id) : undefined;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  return chain;
};
const catName = (id?: string | null) => (id ? CAT_MAP.get(id)?.name.trim() ?? "" : "");

type AttrMap = Record<string, string | string[]>;
const attrsOf = (p: Product): AttrMap => ((p as any).attributes || {}) as AttrMap;
const attrText = (v: string | string[]) => (Array.isArray(v) ? v.join(", ") : String(v));
const TOP_SPECS = ["Brand", "Finish", "Skin Type", "Coverage", "Concentration"];
const specsOf = (p: Product): [string, string][] =>
  Object.entries(attrsOf(p))
    .filter(([k, v]) => k !== "Color" && v != null && attrText(v).trim() !== "")
    .sort(([a], [b]) => {
      const ia = TOP_SPECS.indexOf(a), ib = TOP_SPECS.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    })
    .map(([k, v]) => [k, attrText(v)]);

const money = (v: number) => `€${(Number(v) || 0).toFixed(2)}`;
const isLive = (p: Product) => (p as any).published !== false;
const stockOf = (p: Product) => Math.max(0, Number((p as any).quantity) || 0);

type Variant = { color: string; hex?: string | null; images?: string[]; stock?: { size: string; qty: number }[] };
type PickedOpt = { color?: string };
const variantsOf = (p: Product): Variant[] => (((p as any).variants as Variant[]) || []).filter((v) => v && v.color);
const variantQty = (v: Variant) => (v.stock || []).reduce((s, x) => s + (Number(x.qty) || 0), 0);
const coverOf = (p: Product): string | undefined => (p as any).images?.[0] || variantsOf(p).flatMap((v) => v.images || [])[0];

/* ═══════════════════════════ STYLES ═══════════════════════════ */
const INK = "#000";
const BODY = "#5C5C5C";
const LINE = "#E5E5E5";
const YEL = "#FFD60A";
const RASP = "#E30B5C";
const LIME = "#C8E17A";
const RED = "#E10000";
const HV = [
  { c: YEL, f: INK },
  { c: RASP, f: "#fff" },
  { c: LIME, f: INK },
  { c: RED, f: "#fff" },
];
const cycle = HV.map((h, i) => `.mk-grid > .mk-card:nth-child(4n+${i + 1}), .mk-chips > .mk-chip:nth-child(4n+${i + 1}){ --hs:${h.c}; --hf:${h.f}; }`).join("\n");

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Jost:wght@300;400;500;600&display=swap');
.mk-page, .mk-page *, .mk-page *::before, .mk-page *::after, .mk-portal, .mk-portal *, .mk-portal *::before, .mk-portal *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.mk-page{ --hs:${YEL}; --hf:${INK}; min-height:100vh; background:#fff; color:${INK}; overflow-x:hidden;
  padding:calc(18px + env(safe-area-inset-top)) 16px calc(70px + env(safe-area-inset-bottom)); }
:where(.mk-page, .mk-portal) button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
:where(.mk-page, .mk-portal) button:disabled{ cursor:not-allowed; opacity:.45; }
.mk-page button:focus-visible, .mk-page input:focus-visible, .mk-portal button:focus-visible{ outline:3px solid ${INK}; outline-offset:3px; }
.mk-wrap{ width:100%; max-width:1100px; margin:0 auto; }

.mk-top{ display:flex; align-items:center; gap:12px; }
.mk-top h1{ flex:1; margin:0; font-family:'Cormorant Garamond',serif; font-size:clamp(34px,7vw,52px); font-weight:600; line-height:1; }
.mk-round{ position:relative; width:46px; height:46px; border-radius:50%; border:1px solid ${INK}; display:flex; align-items:center; justify-content:center;
  background:linear-gradient(180deg,#fff,#f0f0f0); box-shadow:inset 0 2px 1px #fff, 0 4px 0 ${INK}; transition:all .2s; }
.mk-round:hover{ background:${YEL}; transform:translateY(-2px); }
.mk-round:active{ transform:translateY(3px); box-shadow:inset 0 2px 1px #fff, 0 1px 0 ${INK}; }
.mk-badge{ position:absolute; top:-7px; right:-7px; min-width:20px; height:20px; padding:0 5px; border-radius:999px; background:${RED}; color:#fff; font-style:normal; font-size:10.5px; font-weight:600;
  display:flex; align-items:center; justify-content:center; border:2px solid #fff; }

/* ── split search rectangle ── */
.mk-search{ display:flex; align-items:stretch; height:58px; margin-top:18px; border:2px solid ${INK}; border-radius:16px; background:#fff; overflow:hidden;
  box-shadow:0 5px 0 ${INK}; transition:box-shadow .2s, transform .2s; }
.mk-search:focus-within{ box-shadow:0 5px 0 ${RASP}; }
.mk-sin{ flex:1; min-width:0; display:flex; align-items:center; gap:8px; padding:0 8px 0 18px; }
.mk-sin input{ flex:1; min-width:0; height:100%; border:none; outline:none; background:transparent; font-size:16px; color:${INK}; }
.mk-sin input::placeholder{ color:#A3A3A3; }
.mk-clear{ display:flex; width:26px; height:26px; border-radius:50%; align-items:center; justify-content:center; background:${INK}; color:#fff; flex:none; transition:background .2s; }
.mk-clear:hover{ background:${RASP}; }
.mk-sact{ flex:none; display:flex; align-items:center; gap:4px; padding:0 8px; border-left:2px solid ${INK}; background:linear-gradient(180deg,#fff,#f4f4f4); }
.mk-ibtn{ --hs:${YEL}; --hf:${INK}; position:relative; width:42px; height:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; color:${INK}; transition:background .2s, color .2s, transform .2s; }
.mk-ibtn:nth-child(2){ --hs:${RASP}; --hf:#fff; }
.mk-ibtn:nth-child(3){ --hs:${LIME}; --hf:${INK}; }
.mk-ibtn:hover{ background:var(--hs); color:var(--hf); transform:translateY(-1px); }
.mk-ibtn:active{ transform:scale(.92); }
.mk-ibtn.live{ background:${RED}; color:#fff; animation:mkPulse 1.1s ease-in-out infinite; }
@keyframes mkPulse{ 0%,100%{ box-shadow:0 0 0 0 rgba(225,0,0,.5); } 50%{ box-shadow:0 0 0 8px rgba(225,0,0,0); } }

.mk-chips{ display:flex; gap:8px; overflow-x:auto; padding:18px 4px 8px; margin:0 -4px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.mk-chips::-webkit-scrollbar{ display:none; }
.mk-chip{ --hs:${YEL}; --hf:${INK}; flex:none; display:inline-flex; align-items:center; gap:8px; height:40px; padding:0 18px; border-radius:999px; border:1px solid ${INK};
  background:linear-gradient(180deg,#fff,#f1f1f1); box-shadow:inset 0 2px 1px #fff, 0 3px 0 ${INK}; font-size:12px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; white-space:nowrap; transition:all .2s; }
.mk-chip small{ font-size:10.5px; font-weight:600; opacity:.6; letter-spacing:.04em; }
.mk-chip:hover{ background:var(--hs); color:var(--hf); transform:translateY(-1px); }
.mk-chip.on{ background:${INK}; color:#fff; }
.mk-chip.on:hover{ background:var(--hs); color:var(--hf); }
.mk-count{ margin:14px 2px 0; color:${BODY}; font-size:13px; }

/* ── product grid ── */
.mk-grid{ display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:16px; margin-top:16px; padding:4px; }
@media (min-width:700px){ .mk-grid{ grid-template-columns:repeat(3, minmax(0,1fr)); gap:22px; } }
@media (min-width:1000px){ .mk-grid{ grid-template-columns:repeat(4, minmax(0,1fr)); } }
.mk-card{ --hs:${YEL}; --hf:${INK}; position:relative; display:flex; flex-direction:column; border:2px solid ${INK}; border-radius:24px; overflow:hidden; background:#fff; text-align:left; cursor:pointer;
  box-shadow:0 5px 0 ${INK}; transition:transform .25s cubic-bezier(.2,.9,.3,1.2), box-shadow .25s; }
.mk-card:hover{ transform:translateY(-6px); box-shadow:0 0 0 4px var(--hs), 0 0 0 6px ${INK}, 0 22px 30px -16px rgba(0,0,0,.5); }
.mk-media{ position:relative; aspect-ratio:1/1; background:#F4F1EA; display:flex; align-items:center; justify-content:center; color:#6B6B6B; overflow:hidden; border-bottom:2px solid ${INK}; }
.mk-media img{ width:100%; height:100%; object-fit:cover; display:block; transition:transform .5s cubic-bezier(.22,.8,.26,1); }
.mk-card:hover .mk-media img{ transform:scale(1.06); }
.mk-media::before{ content:""; position:absolute; z-index:1; top:6px; left:12%; right:12%; height:18px; border-radius:999px; pointer-events:none; background:linear-gradient(180deg,rgba(255,255,255,.7),rgba(255,255,255,0)); }
.mk-tag{ position:absolute; top:10px; left:10px; z-index:2; background:${RED}; color:#fff; font-size:9.5px; font-weight:600; letter-spacing:.12em; text-transform:uppercase; padding:4px 10px; border-radius:999px; }
.mk-add{ position:absolute; right:10px; bottom:10px; z-index:2; width:42px; height:42px; border-radius:50%; background:linear-gradient(180deg,#fff,#ececec); color:${INK}; display:flex; align-items:center; justify-content:center;
  box-shadow:inset 0 2px 1px #fff, 0 3px 0 rgba(0,0,0,.4); transition:all .2s; }
.mk-add:hover:not(:disabled){ background:var(--hs); color:var(--hf); transform:scale(1.1); }
.mk-body{ display:flex; flex-direction:column; gap:4px; padding:12px 14px 14px; min-width:0; }
.mk-sub{ font-size:10px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${BODY}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.mk-name{ font-family:'Cormorant Garamond',serif; font-size:21px; font-weight:700; line-height:1.15; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.mk-brand{ font-size:12.5px; color:${BODY}; }
.mk-meta{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-top:6px; }
.mk-price{ display:inline-flex; padding:3px 12px; border-radius:999px; background:linear-gradient(180deg,#fff,#ececec); border:1px solid ${INK}; box-shadow:0 2px 0 ${INK}; font-size:15px; font-weight:600; transition:background .2s, color .2s; }
.mk-card:hover .mk-price{ background:var(--hs); color:var(--hf); }
.mk-shades{ display:flex; align-items:center; }
.mk-shades .sw + .sw{ margin-left:-6px; }
.mk-shades small{ margin-left:6px; font-size:11px; color:${BODY}; white-space:nowrap; }
.sw{ --c:#ccc; display:inline-block; width:20px; height:20px; border-radius:50%; flex:none;
  background:
    radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.9) 6%, rgba(255,255,255,0) 26%),
    radial-gradient(circle at 36% 30%, color-mix(in srgb, var(--c) 72%, #fff) 0, var(--c) 48%, color-mix(in srgb, var(--c) 58%, #000) 100%);
  box-shadow:inset -2px -3px 4px rgba(0,0,0,.3), 0 0 0 2px #fff, 0 0 0 3px rgba(0,0,0,.18); }
.sw.multi{ background:radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,0) 26%), conic-gradient(#ff5c5c,#ffb347,#ffe94d,#5ddc6c,#4dd8ff,#6d7cff,#c86bff,#ff5c5c); }
.mk-specs{ display:flex; flex-wrap:wrap; gap:5px; margin-top:6px; }
.mk-spec{ font-size:9.5px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; padding:3px 9px; border-radius:999px; border:1px solid #D4D4D4; color:${BODY}; }

.mk-empty{ display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:70px 20px; }
.mk-empty h3{ font-family:'Cormorant Garamond',serif; font-size:30px; font-weight:600; margin:6px 0 0; }
.mk-empty p{ margin:0; color:${BODY}; }

.mk-btn{ --hs:${YEL}; --hf:${INK}; position:relative; overflow:hidden; display:inline-flex; align-items:center; justify-content:center; gap:8px; height:50px; padding:0 28px; border-radius:999px; border:1px solid #000;
  background:linear-gradient(180deg,#4a4a4a 0%,#111 48%,#000 100%); color:#fff !important; font-size:11.5px; font-weight:600; letter-spacing:.2em; text-transform:uppercase;
  box-shadow:inset 0 2px 2px rgba(255,255,255,.4), inset 0 -4px 6px rgba(0,0,0,.55), 0 5px 0 #2b2b2b; transition:all .2s; }
.mk-btn:hover:not(:disabled){ background:var(--hs); color:var(--hf) !important; transform:translateY(-2px); box-shadow:inset 0 2px 2px rgba(255,255,255,.7), 0 7px 0 ${INK}; }
.mk-btn:active:not(:disabled){ transform:translateY(4px); box-shadow:0 1px 0 ${INK}; }
.mk-btn.ghost{ background:linear-gradient(180deg,#fff,#e9e9e9); color:${INK} !important; box-shadow:inset 0 2px 2px #fff, 0 5px 0 ${INK}; --hs:${RASP}; --hf:#fff; }
.mk-btn.add{ --hs:${LIME}; --hf:${INK}; }
.mk-btn.block{ width:100%; }

.mk-toast{ position:fixed; left:50%; bottom:calc(28px + env(safe-area-inset-bottom)); transform:translateX(-50%); z-index:600; max-width:calc(100% - 32px); background:#fff; color:${INK}; border:1px solid ${INK};
  font-size:11.5px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; padding:12px 22px; border-radius:999px; box-shadow:0 0 0 3px ${YEL}, 0 0 0 4px ${INK}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; animation:mkToast .25s ease-out; }
@keyframes mkToast{ from{ opacity:0; transform:translate(-50%,10px); } to{ opacity:1; transform:translate(-50%,0); } }

/* ── sheets ── */
.mk-scrim{ position:fixed; inset:0; z-index:400; display:flex; background:rgba(0,0,0,.5); animation:mkFade .2s ease-out; }
.mk-scrim.desktop{ align-items:center; justify-content:center; padding:24px; }
.mk-scrim.mobile{ align-items:flex-end; justify-content:center; }
.mk-panel{ position:relative; display:flex; flex-direction:column; width:100%; background:#fff; color:${INK}; border:1px solid ${INK}; }
.mk-panel::after{ content:""; position:absolute; inset:6px; border:1px solid ${INK}; pointer-events:none; z-index:5; }
.mk-panel.desktop{ max-width:480px; max-height:88vh; border-radius:30px; animation:mkPop .25s cubic-bezier(.2,.9,.3,1.1); }
.mk-panel.desktop::after{ border-radius:24px; }
.mk-panel.desktop.wide{ max-width:900px; }
.mk-panel.desktop.ai{ max-width:560px; height:80vh; }
.mk-panel.mobile{ max-height:92vh; border-radius:28px 28px 0 0; padding-bottom:env(safe-area-inset-bottom); animation:mkSlide .3s cubic-bezier(.2,.9,.3,1); }
.mk-panel.mobile.ai{ height:92vh; }
.mk-panel.mobile::after{ border-radius:22px 22px 0 0; border-bottom:none; }
.mk-handle{ width:42px; height:4px; border-radius:999px; background:#CFCFCF; margin:14px auto 0; flex-shrink:0; position:relative; z-index:6; }
.mk-scroll{ flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.mk-x{ position:absolute; top:16px; right:16px; z-index:7; width:38px; height:38px; border-radius:50%; background:linear-gradient(180deg,#fff,#eee) !important; border:1px solid ${INK} !important; display:flex; align-items:center; justify-content:center; transition:all .2s; }
.mk-x:hover{ background:${RASP} !important; color:#fff; }
@keyframes mkFade{ from{ opacity:0; } to{ opacity:1; } }
@keyframes mkPop{ from{ opacity:0; transform:scale(.95) translateY(12px); } to{ opacity:1; transform:scale(1) translateY(0); } }
@keyframes mkSlide{ from{ transform:translateY(100%); } to{ transform:translateY(0); } }

.mk-detail{ display:grid; grid-template-columns:1fr; gap:20px; padding:20px 20px 26px; }
.mk-panel.desktop.wide .mk-detail{ grid-template-columns:1fr 1fr; gap:30px; padding:24px; align-items:start; }
.mk-gal-main{ position:relative; background:#F4F1EA; border:1px solid ${INK}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${INK}; border-radius:18px; aspect-ratio:1/1; overflow:hidden; display:flex; align-items:center; justify-content:center; color:#6B6B6B; margin-top:6px; }
.mk-gal-main img{ width:100%; height:100%; object-fit:contain; display:block; }
.mk-nav{ position:absolute; top:50%; transform:translateY(-50%); width:38px; height:38px; border-radius:50%; background:linear-gradient(180deg,#fff,#e9e9e9); display:flex; align-items:center; justify-content:center; box-shadow:0 3px 0 rgba(0,0,0,.3); transition:all .2s; }
.mk-nav:hover{ background:${YEL}; }
.mk-nav.l{ left:10px; } .mk-nav.r{ right:10px; }
.mk-thumbs{ display:flex; gap:8px; margin-top:12px; overflow-x:auto; scrollbar-width:none; }
.mk-thumbs::-webkit-scrollbar{ display:none; }
.mk-thumb{ flex:none; width:56px; height:56px; border-radius:12px; overflow:hidden; background:#F4F1EA; border:2px solid transparent; padding:0; transition:border-color .2s; }
.mk-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.mk-thumb:hover{ border-color:${YEL}; }
.mk-thumb.on{ border-color:${INK}; }
.mk-side{ display:flex; flex-direction:column; min-width:0; }
.mk-crumb{ font-size:10.5px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; color:${BODY}; padding-right:48px; }
.mk-pname{ font-family:'Cormorant Garamond',serif; font-weight:700; font-size:clamp(30px,5vw,42px); line-height:1.05; margin:10px 0 6px; }
.mk-pprice{ font-size:22px; font-weight:600; }
.mk-desc{ margin:14px 0 0; font-size:15px; line-height:1.65; color:#333; white-space:pre-line; }
.mk-info{ display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:14px; }
.mk-info div{ border:1px solid ${LINE}; border-radius:12px; padding:9px 12px; }
.mk-info small{ display:block; font-size:9.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:${BODY}; margin-bottom:2px; }
.mk-info span{ font-size:13px; }
.mk-opt{ margin-top:16px; }
.mk-opt small{ display:block; font-size:10px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${BODY}; margin-bottom:8px; }
.mk-opt small b{ color:${INK}; font-weight:600; }
.mk-dots{ display:flex; flex-wrap:wrap; gap:10px; }
.mk-dot{ width:42px; height:42px; border-radius:50%; padding:4px; border:2px solid transparent; transition:border-color .2s, transform .25s cubic-bezier(.2,.9,.3,1.4); }
.mk-dot .sw{ width:100%; height:100%; box-shadow:inset -3px -4px 7px rgba(0,0,0,.32), 0 0 0 1px rgba(0,0,0,.12); }
.mk-dot:hover{ transform:translateY(-3px) scale(1.08); border-color:${LIME}; }
.mk-dot.on{ border-color:${INK}; }
.mk-dot.gone{ opacity:.4; }
.mk-stock{ display:inline-flex; align-self:flex-start; margin-top:16px; font-size:10px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; padding:5px 12px; border-radius:999px; border:1px solid #CFCFCF; color:${BODY}; }
.mk-stock.low{ border-color:${RED}; color:${RED}; }
.mk-note{ margin:12px 0 0; font-size:12px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; color:${RED}; }
.mk-total{ display:flex; align-items:baseline; justify-content:space-between; margin-top:20px; padding-top:14px; border-top:1px solid ${LINE}; }
.mk-total small{ font-size:10px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:${BODY}; }
.mk-total b{ font-family:'Cormorant Garamond',serif; font-size:30px; font-weight:600; }
.mk-buyrow{ display:flex; align-items:center; gap:12px; margin-top:12px; }
.mk-qty{ display:inline-flex; align-items:center; height:48px; border:1px solid ${INK}; border-radius:999px; overflow:hidden; }
.mk-qty button{ width:42px; height:100%; display:flex; align-items:center; justify-content:center; transition:background .2s; }
.mk-qty button:hover:not(:disabled){ background:${YEL}; }
.mk-qty b{ min-width:26px; text-align:center; font-weight:500; font-size:15px; }
.mk-actions{ display:flex; flex-direction:column; gap:10px; margin-top:12px; }

.mk-ai-head{ padding:22px 22px 8px; font-family:'Cormorant Garamond',serif; font-size:30px; font-weight:600; display:flex; align-items:center; gap:10px; }
.mk-ai-body{ padding:0 14px 14px; }

@media (prefers-reduced-motion: reduce){
  .mk-scrim, .mk-panel, .mk-toast, .mk-ibtn.live{ animation:none !important; }
  .mk-page *, .mk-portal *{ transition:none !important; }
}
${cycle}
`;

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= breakpoint : false);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
}

/* ═══════════════════════════ SHEET ═══════════════════════════ */
const Sheet: React.FC<{ label: string; onClose: () => void; wide?: boolean; ai?: boolean; children: React.ReactNode }> = ({ label, onClose, wide, ai, children }) => {
  const isMobile = useIsMobile();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return createPortal(
    <div className={`mk-portal mk-scrim ${isMobile ? "mobile" : "desktop"}`} onClick={onClose}>
      <div
        role="dialog" aria-modal="true" aria-label={label}
        className={`mk-panel ${isMobile ? "mobile" : "desktop"}${wide ? " wide" : ""}${ai ? " ai" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && <div className="mk-handle" />}
        <button type="button" className="mk-x" aria-label="Close" onClick={onClose}><X size={18} strokeWidth={1.8} /></button>
        <div className="mk-scroll">{children}</div>
      </div>
    </div>,
    document.body
  );
};

const Swatch: React.FC<{ hex?: string | null }> = ({ hex }) => (
  <span className={`sw${hex ? "" : " multi"}`} style={hex ? ({ "--c": hex } as React.CSSProperties) : undefined} />
);

/* ═══════════════════════════ PRODUCT DETAIL ═══════════════════════════ */
const Detail: React.FC<{
  p: Product;
  inBag: number;
  onAdd: (qty: number, pick?: PickedOpt) => void;
  onBuy: (qty: number, pick?: PickedOpt) => void;
  onInc: (pick?: PickedOpt) => void;
  onRemove: () => void;
}> = ({ p, inBag, onAdd, onBuy, onInc, onRemove }) => {
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const variants = variantsOf(p);
  const [color, setColor] = useState<string | null>(() => (variants.find((v) => variantQty(v) > 0) ?? variants[0])?.color ?? null);
  const cur = variants.find((v) => v.color === color);
  const mainImgs: string[] = (p as any).images ?? [];
  const imgs: string[] = cur?.images?.length ? cur.images : mainImgs.length ? mainImgs : variants.flatMap((v) => v.images || []);

  const stock = stockOf(p);
  const price = Number((p as any).price) || 0;
  const pickQty = cur ? variantQty(cur) : Infinity;
  const room = Math.max(0, Math.min(stock - inBag, pickQty));
  const maxed = room <= 0;
  const q = Math.min(qty, Math.max(1, room));
  const pick: PickedOpt = { color: cur?.color };
  const catId = idOf((p as any).subcategoryId) || idOf((p as any).categoryId);
  const crumb = chainIdsOf(catId).slice(1).map(catName).filter(Boolean).join(" · ");
  const specs = specsOf(p);
  const where = [(p as any).city, (p as any).country].filter(Boolean).join(", ");
  const desc = String((p as any).description || "").trim();

  const step = (d: number) => setQty(Math.min(Math.max(1, q + d), Math.max(1, room)));
  const go = (d: number) => { if (imgs.length) setIdx((i) => (i + d + imgs.length) % imgs.length); };

  return (
    <div className="mk-detail">
      <div>
        <div className="mk-gal-main">
          {imgs[idx] ? <img src={imgs[idx]} alt={p.name} /> : <PackageOpen size={46} strokeWidth={1.1} />}
          {imgs.length > 1 && (
            <>
              <button type="button" className="mk-nav l" aria-label="Previous photo" onClick={() => go(-1)}><ChevronLeft size={18} /></button>
              <button type="button" className="mk-nav r" aria-label="Next photo" onClick={() => go(1)}><ChevronRight size={18} /></button>
            </>
          )}
        </div>
        {imgs.length > 1 && (
          <div className="mk-thumbs">
            {imgs.map((u, i) => (
              <button type="button" key={u + i} className={`mk-thumb ${i === idx ? "on" : ""}`} aria-label={`Photo ${i + 1}`} onClick={() => setIdx(i)}><img src={u} alt="" /></button>
            ))}
          </div>
        )}
      </div>

      <div className="mk-side">
        {crumb && <span className="mk-crumb">{crumb}</span>}
        <h2 className="mk-pname">{p.name}</h2>
        <span className="mk-pprice">{money(price)}</span>
        {desc && <p className="mk-desc">{desc}</p>}

        {(specs.length > 0 || where || inBag > 0) && (
          <div className="mk-info">
            {specs.map(([k, v]) => <div key={k}><small>{k}</small><span>{v}</span></div>)}
            {where && <div><small>Ships from</small><span>{where}</span></div>}
            {inBag > 0 && <div><small>In your cart</small><span>{inBag}</span></div>}
          </div>
        )}

        {variants.length > 0 && (
          <div className="mk-opt">
            <small>Shade{cur ? <>: <b>{cur.color}</b></> : null}</small>
            <div className="mk-dots">
              {variants.map((v) => (
                <button
                  type="button" key={v.color} aria-label={v.color} aria-pressed={v.color === color}
                  className={`mk-dot${v.color === color ? " on" : ""}${variantQty(v) <= 0 ? " gone" : ""}`}
                  onClick={() => { setColor(v.color); setIdx(0); setQty(1); }}
                >
                  <Swatch hex={v.hex} />
                </button>
              ))}
            </div>
          </div>
        )}

        <span className={`mk-stock ${stock <= 5 ? "low" : ""}`}>{stock <= 5 ? `Only ${stock} left` : "In stock"}</span>
        {maxed && <p className="mk-note">You already have all of this shade in your cart</p>}

        <div className="mk-total">
          <small>{inBag > 0 ? "In your cart" : "Total"}</small>
          <b>{money(price * (inBag > 0 ? inBag : q))}</b>
        </div>

        <div className="mk-buyrow">
          {inBag > 0 ? (
            <div className="mk-qty">
              <button type="button" aria-label="Remove one from cart" onClick={onRemove}><Minus size={16} /></button>
              <b>{inBag}</b>
              <button type="button" aria-label="Add one to cart" disabled={maxed} onClick={() => onInc(pick)}><Plus size={16} /></button>
            </div>
          ) : (
            <>
              <div className="mk-qty">
                <button type="button" aria-label="Less" disabled={maxed || q <= 1} onClick={() => step(-1)}><Minus size={16} /></button>
                <b>{q}</b>
                <button type="button" aria-label="More" disabled={maxed || q >= room} onClick={() => step(1)}><Plus size={16} /></button>
              </div>
              <button type="button" className="mk-btn add block" disabled={maxed} onClick={() => onAdd(q, pick)}><ShoppingBag size={16} /> Add to cart</button>
            </>
          )}
        </div>
        <div className="mk-actions">
          <button type="button" className="mk-btn ghost block" disabled={stock <= 0} onClick={() => (inBag > 0 ? onBuy(0, pick) : onBuy(q, pick))}>
            {inBag > 0 ? "Go to checkout" : "Buy now"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════ MAKEUP PAGE ═══════════════════════════ */
export default function Makeup() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allProducts = useSelector((s: any) => s.products?.products) as Product[] | undefined;
  const cartItems = useSelector(selectCartItems);

  const [q, setQ] = useState("");
  const [sub, setSub] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const recRef = useRef<any>(null);

  useEffect(() => {
    const unsub = dispatch(listenToPublishedProducts() as any) as any;
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* already stopped */ } }, []);

  /* only published, in-stock MAKEUP products */
  const makeup = useMemo(
    () =>
      (allProducts ?? [])
        .filter((p) => isLive(p) && stockOf(p) > 0)
        .map((p) => ({ p, chain: new Set<string>([...chainIdsOf(idOf((p as any).categoryId)), ...chainIdsOf(idOf((p as any).subcategoryId))]) }))
        .filter(({ chain }) => chain.has(MAKEUP_ID)),
    [allProducts]
  );

  const subs = useMemo(() => childrenOf(MAKEUP_ID), []);
  const subCount = (id: string) => makeup.filter((m) => m.chain.has(id)).length;

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return makeup
      .filter(({ p, chain }) => {
        if (sub && !chain.has(sub)) return false;
        if (!term) return true;
        const hay = `${p.name ?? ""} ${(p as any).description ?? ""} ${catName(idOf((p as any).subcategoryId) || idOf((p as any).categoryId))} ${Object.values(attrsOf(p)).flat().join(" ")} ${variantsOf(p).map((v) => v.color).join(" ")}`.toLowerCase();
        return hay.includes(term);
      })
      .map((m) => m.p)
      .sort((a, b) => ((b as any).createdAt ?? 0) - ((a as any).createdAt ?? 0));
  }, [makeup, q, sub]);

  const openProduct = openId ? (allProducts ?? []).find((p) => p.productId === openId) : undefined;

  /* ── search bar actions ── */
  const runSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    inputRef.current?.blur();
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setToast(`${shown.length} ${shown.length === 1 ? "result" : "results"}`);
  };

  const startVoice = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { setToast("Voice search isn't supported here"); return; }
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } return; }
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (e: any) => { const text = e.results?.[0]?.[0]?.transcript; if (text) setQ(String(text).replace(/[.!?]+$/, "")); };
    r.onerror = () => { setListening(false); setToast("Couldn't hear you, try again"); };
    r.onend = () => setListening(false);
    recRef.current = r;
    setListening(true);
    try { r.start(); } catch { setListening(false); }
  };

  /* ── cart ── */
  const cartCount = cartItems.reduce((s: number, i: any) => s + lineQty(i), 0);
  const inBagOf = (productId: string) => cartItems.filter((i: any) => lineId(i) === productId).reduce((s: number, i: any) => s + lineQty(i), 0);

  const buildLine = (p: Product, quantity: number, pick?: PickedOpt) => {
    const v = pick?.color ? variantsOf(p).find((x) => x.color === pick.color) : undefined;
    return {
      productId: p.productId,
      name: p.name,
      price: Number((p as any).price) || 0,
      quantity,
      photo: v?.images?.[0] || coverOf(p) || "",
      ownerId: (p as any).ownerId ?? "",
      ...(pick?.color ? { color: pick.color } : {}),
    };
  };
  const addLine = (p: Product, quantity: number, pick?: PickedOpt) => dispatch(addToCart(buildLine(p, quantity, pick) as any) as any);

  const goToCheckout = (items: any[] = cartItems) => {
    if (!items.length) { setToast("Your cart is empty"); return; }
    navigate("/checkout", { state: { items } });
  };

  const add = (p: Product, qty = 1, pick?: PickedOpt): boolean => {
    const room = stockOf(p) - inBagOf(p.productId);
    if (room <= 0) { setToast("All available pieces are in your cart"); return false; }
    addLine(p, Math.min(qty, room), pick);
    setToast(`${p.name} added to cart`);
    return true;
  };

  const buy = (p: Product, qty: number, pick?: PickedOpt) => {
    setOpenId(null);
    const room = stockOf(p) - inBagOf(p.productId);
    if (qty <= 0 || room <= 0) { goToCheckout(); return; }
    const n = Math.min(qty, room);
    const line = buildLine(p, n, pick);
    addLine(p, n, pick);
    const idx = cartItems.findIndex((i: any) => lineId(i) === line.productId);
    const next = idx < 0 ? [...cartItems, line] : cartItems.map((i: any, k: number) => (k === idx ? { ...i, quantity: lineQty(i) + n } : i));
    goToCheckout(next);
  };

  const quickAdd = (p: Product) => {
    const vs = variantsOf(p);
    const v = vs.find((x) => variantQty(x) > 0) ?? vs[0];
    add(p, 1, v ? { color: v.color } : undefined);
  };

  const loading = allProducts === undefined;

  return (
    <div className="mk-page">
      <style>{CSS}</style>
      <div className="mk-wrap">
        <div className="mk-top">
          <button type="button" className="mk-round" aria-label="Back" onClick={() => navigate("/explore")}><ArrowLeft size={19} strokeWidth={1.8} /></button>
          <h1>Makeup</h1>
          <button type="button" className="mk-round" aria-label="Open cart" onClick={() => goToCheckout()}>
            <ShoppingBag size={19} strokeWidth={1.8} />
            {cartCount > 0 && <i className="mk-badge">{cartCount > 99 ? "99+" : cartCount}</i>}
          </button>
        </div>

        {/* rectangle search: input on the left, search / AI / voice on the far right */}
        <form className="mk-search" role="search" onSubmit={runSearch}>
          <div className="mk-sin">
            <input
              ref={inputRef} type="text" value={q} onChange={(e) => setQ(e.target.value)} enterKeyHint="search"
              placeholder="Search lipstick, foundation, shade…" aria-label="Search makeup"
            />
            {q && <button type="button" className="mk-clear" aria-label="Clear search" onClick={() => { setQ(""); inputRef.current?.focus(); }}><X size={14} strokeWidth={2.4} /></button>}
          </div>
          <div className="mk-sact">
            <button type="submit" className="mk-ibtn" aria-label="Search"><Search size={22} strokeWidth={2.2} /></button>
            <button type="button" className="mk-ibtn" aria-label="Ask AI" onClick={() => setAiOpen(true)}><Sparkles size={22} strokeWidth={2} /></button>
            <button type="button" className={`mk-ibtn${listening ? " live" : ""}`} aria-label={listening ? "Stop listening" : "Voice search"} aria-pressed={listening} onClick={startVoice}><Mic size={22} strokeWidth={2} /></button>
          </div>
        </form>

        {subs.length > 0 && (
          <div className="mk-chips">
            <button type="button" className={`mk-chip${!sub ? " on" : ""}`} onClick={() => setSub(null)}>All makeup <small>{makeup.length}</small></button>
            {subs.map((s) => (
              <button type="button" key={s.id} className={`mk-chip${sub === s.id ? " on" : ""}`} onClick={() => setSub(sub === s.id ? null : s.id)}>
                {s.name.trim()} <small>{subCount(s.id)}</small>
              </button>
            ))}
          </div>
        )}

        <div ref={gridRef} style={{ scrollMarginTop: 12 }}>
          {!loading && shown.length > 0 && <p className="mk-count">{shown.length} {shown.length === 1 ? "product" : "products"}</p>}

          {loading ? (
            <div className="mk-empty"><PackageOpen size={46} strokeWidth={1.2} /><h3>Loading makeup</h3></div>
          ) : shown.length === 0 ? (
            <div className="mk-empty">
              <PackageOpen size={46} strokeWidth={1.2} />
              <h3>{makeup.length ? "No makeup matches" : "No makeup yet"}</h3>
              <p>{makeup.length ? "Try another word or category." : "New makeup is on the way."}</p>
              {(q || sub) && <button type="button" className="mk-btn ghost" onClick={() => { setQ(""); setSub(null); }}>Clear search</button>}
            </div>
          ) : (
            <div className="mk-grid">
              {shown.map((p) => {
                const stock = stockOf(p);
                const vs = variantsOf(p);
                const cover = coverOf(p);
                const attrs = attrsOf(p);
                const brand = typeof attrs["Brand"] === "string" ? (attrs["Brand"] as string) : "";
                const subLabel = catName(idOf((p as any).subcategoryId) || idOf((p as any).categoryId));
                const tags = specsOf(p).filter(([k]) => k !== "Brand").slice(0, 2).map(([, v]) => v.split(", ")[0]);
                return (
                  <div
                    key={p.productId} className="mk-card" role="button" tabIndex={0}
                    onClick={() => setOpenId(p.productId)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(p.productId); } }}
                  >
                    <div className="mk-media">
                      {cover ? <img src={cover} alt={p.name} loading="lazy" draggable={false} /> : <PackageOpen size={34} strokeWidth={1.1} />}
                      {stock <= 5 && <span className="mk-tag">Only {stock} left</span>}
                      <button type="button" className="mk-add" aria-label={`Add ${p.name} to cart`} onClick={(e) => { e.stopPropagation(); quickAdd(p); }}><Plus size={20} strokeWidth={2.4} /></button>
                    </div>
                    <div className="mk-body">
                      {subLabel && <span className="mk-sub">{subLabel}</span>}
                      <b className="mk-name">{p.name}</b>
                      {brand && <span className="mk-brand">{brand}</span>}
                      {tags.length > 0 && <div className="mk-specs">{tags.map((t) => <span key={t} className="mk-spec">{t}</span>)}</div>}
                      <div className="mk-meta">
                        <span className="mk-price">{money(Number((p as any).price) || 0)}</span>
                        {vs.length > 0 && (
                          <span className="mk-shades" title={`${vs.length} shades`}>
                            {vs.slice(0, 4).map((v) => <Swatch key={v.color} hex={v.hex} />)}
                            {vs.length > 4 && <small>+{vs.length - 4}</small>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast && <div className="mk-toast" role="status">{toast}</div>}

      {openProduct && (
        <Sheet label={openProduct.name} wide onClose={() => setOpenId(null)}>
          <Detail
            key={openProduct.productId}
            p={openProduct}
            inBag={inBagOf(openProduct.productId)}
            onAdd={(n, pick) => { if (add(openProduct, n, pick)) setOpenId(null); }}
            onBuy={(n, pick) => buy(openProduct, n, pick)}
            onInc={(pick) => { add(openProduct, 1, pick); }}
            onRemove={() => dispatch(decrementCartItem({ productId: openProduct.productId } as any) as any)}
          />
        </Sheet>
      )}

      {aiOpen && (
        <Sheet label="AI beauty assistant" ai onClose={() => setAiOpen(false)}>
          <div className="mk-ai-head"><Sparkles size={26} /> Ask AI</div>
          <div className="mk-ai-body"><BAi open={false} onClose={function (): void {
                      throw new Error("Function not implemented.");
                  } } products={[]} /></div>
        </Sheet>
      )}
    </div>
  );
}